import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

function HeroSection() {
  const { t } = useTranslation();
  const [currentReview, setCurrentReview] = useState(0);
  const [navbarHeight, setNavbarHeight] = useState(0);

  // Calculate navbar height dynamically
  useEffect(() => {
    const calculateNavbarHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        setNavbarHeight(header.offsetHeight);
      }
    };

    calculateNavbarHeight();
    window.addEventListener('resize', calculateNavbarHeight);
    window.addEventListener('scroll', calculateNavbarHeight);
    
    return () => {
      window.removeEventListener('resize', calculateNavbarHeight);
      window.removeEventListener('scroll', calculateNavbarHeight);
    };
  }, []);

  // Reviews data for animation
  const reviews = [
    { rating: '4.9', text: 'Google Reviews', subtext: '1,200+ Happy Patients' },
    { rating: '4.8', text: 'Facebook Reviews', subtext: '850+ Satisfied Clients' },
    { rating: '5.0', text: 'Practo Reviews', subtext: '500+ Verified Reviews' },
  ];

  // Auto-cycle through reviews
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % reviews.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [reviews.length]);

  return (
    <section 
      className="relative w-full overflow-hidden"
      style={{ 
        height: `calc(100vh - ${navbarHeight}px)`,
        minHeight: '600px'
      }}
    >
      {/* Background Video */}
      <div className="absolute inset-0 h-full w-full overflow-hidden">
        <video
          className="absolute inset-0 w-full h-full object-cover scale-105"
          src="/assets/onedrive/test.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onLoadedMetadata={(e) => {
            try {
              e.currentTarget.currentTime = 3;
            } catch (err) {
              // no-op
            }
          }}
        />
        {/* Enhanced gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      </div>

      {/* Top-left: Smaller Logo + Years Badge - Repositioned */}
      <div className="absolute top-4 left-4 z-30 animate-fade-in-down">
        <div className="flex items-center gap-3 bg-white/95 backdrop-blur-md px-4 py-2.5 rounded-xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div className="relative">
            <img
              src="/assets/onedrive/logo.png"
              alt="Sharma Ortho Center logo"
              className="w-8 h-8 rounded-lg object-contain bg-white shadow-sm"
            />
            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
          </div>
          <div>
            <div className="text-sm font-bold text-primary-700 tracking-tight">{t('header.yearsExperience')}</div>
            <div className="text-xs text-gray-600 font-medium">{t('header.trustedCare')}</div>
          </div>
        </div>
      </div>

      {/* Center: Enhanced main content with responsive scaling */}
      <div className="relative z-30 h-full">
        <div className="h-full w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          
          {/* Left Side: Text Content */}
          <div className="text-white flex-1 max-w-2xl lg:max-w-3xl">
            {/* Enhanced typography with responsive scaling */}
            <div className="space-y-4 sm:space-y-6 animate-fade-in-up animation-delay-200">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight mb-4 sm:mb-6 bg-gradient-to-r from-white via-white to-blue-200 bg-clip-text text-transparent transition-all duration-300">
                {t('hero.title')}
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 mb-6 sm:mb-8 lg:mb-10 leading-relaxed font-light transition-all duration-300">
                {t('hero.subtitle')}
              </p>
              
              {/* Enhanced CTA buttons matching reference design */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 animate-fade-in-up animation-delay-400">
                <Link
                  to="/contact"
                  className="group relative bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-2xl font-bold text-lg sm:text-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300 shadow-2xl hover:shadow-blue-500/30 transform hover:scale-105 hover:-translate-y-1 overflow-hidden min-w-[200px] justify-center"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {t('hero.bookAppointment')}
                    <svg className="ml-3 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                </Link>
                
                <a
                  href="tel:+917727048951"
                  className="group inline-flex items-center justify-center px-8 sm:px-10 py-4 sm:py-5 bg-white/15 backdrop-blur-md border-2 border-white/40 text-white font-bold text-lg sm:text-xl rounded-2xl hover:bg-white/25 hover:border-white/60 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 hover:-translate-y-1 min-w-[180px]"
                >
                  <Phone className="mr-3 h-6 w-6 sm:h-7 sm:w-7 group-hover:animate-bounce" />
                  {t('hero.callNow')}
                </a>
              </div>
            </div>
          </div>

          {/* Right Side: Enhanced Doctor Image - Larger Size */}
          <div className="hidden lg:block flex-shrink-0 ml-8 xl:ml-16 animate-fade-in-up">
            <div className="relative">
              <img
                src="/assets/onedrive/doc.png"
                alt="Expert Orthopedic Surgeon"
                className="w-72 h-96 xl:w-80 xl:h-[28rem] 2xl:w-96 2xl:h-[32rem] object-cover rounded-3xl shadow-2xl border-4 border-white/30 hover:scale-105 transition-all duration-500 hover:shadow-3xl"
              />
              {/* Enhanced glowing ring effect */}
              <div className="absolute inset-0 rounded-3xl border-4 border-blue-400/40 animate-pulse shadow-lg"></div>
              <div className="absolute -inset-2 rounded-3xl border-2 border-white/20 animate-pulse" style={{animationDelay: '1s'}}></div>
              
            </div>
          </div>

          {/* Mobile Doctor Image - Enhanced for mobile */}
          <div className="lg:hidden absolute top-1/2 right-4 transform -translate-y-1/2 animate-fade-in-up opacity-40">
            <div className="relative">
              <img
                src="/assets/onedrive/doc.png"
                alt="Expert Orthopedic Surgeon"
                className="w-36 h-48 sm:w-44 sm:h-56 object-cover rounded-2xl shadow-2xl border-2 border-white/30"
              />
              
            </div>
          </div>
        </div>
      </div>

      {/* Bottom owners section removed as requested */}

      {/* Enhanced Certification Logos - Smaller with Circular Containers */}
      <div className="hidden sm:flex items-center gap-3 absolute top-4 right-4 z-30 animate-fade-in-down animation-delay-300">
        <div className="bg-white/95 backdrop-blur-md p-2 rounded-full shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 hover:scale-105">
          <img
            src="/assets/onedrive/NABH Logo.jpg"
            alt="NABH Accredited"
            className="h-10 w-10 rounded-full shadow-sm object-contain"
          />
        </div>
        <div className="bg-white/95 backdrop-blur-md p-2 rounded-full shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 hover:scale-105">
          <img
            src="/assets/onedrive/MAA-Y-removebg-preview.png"
            alt="ISO Certified"
            className="h-10 w-10 rounded-full shadow-sm object-contain"
          />
        </div>
      </div>

      {/* Enhanced Bottom-right: Animated Reviews Box - Smaller */}
      <div className="absolute bottom-4 right-4 z-30 animate-fade-in-up animation-delay-500">
        <div className="flex items-center gap-3 bg-white/98 backdrop-blur-md px-4 py-3 rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-3 w-3 text-yellow-400 fill-yellow-400 animate-pulse" style={{animationDelay: `${i * 100}ms`}} />
              ))}
            </div>
            <div 
              className="text-sm font-bold text-gray-800 tracking-tight transition-all duration-500 ease-in-out"
              key={currentReview}
            >
              {reviews[currentReview].rating} {reviews[currentReview].text}
            </div>
            <div 
              className="text-xs text-gray-600 font-medium transition-all duration-500 ease-in-out"
              key={`${currentReview}-sub`}
            >
              {reviews[currentReview].subtext}
            </div>
          </div>
          <div className="relative group flex-shrink-0">
            <img
              className="w-12 h-12 rounded-xl shadow-md group-hover:scale-110 transition-transform duration-300"
              alt="Scan to review us"
              src={
                'https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=https://maps.google.com/?q=Sharma+Ortho+Center+Hospital'
              }
            />
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-green-500/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        </div>
      </div>

      {/* Floating actions removed as requested */}
    </section>
  );
}

export default HeroSection;


